import React, { useState, useEffect, useRef } from 'react';
import { chatAPI } from '../../api/client';
import {
  PaperAirplaneIcon, TrashIcon,
  ArrowsPointingOutIcon, ArrowsPointingInIcon,
  EnvelopeIcon,
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import toast from 'react-hot-toast';
import SendInterviewEmailModal from './SendInterviewEmailModal';
import { parseScreeningMarkdown } from '../../utils/parseScreeningMarkdown';

export default function ChatInterface({
  projectId, indexedCount = 0, totalDocs = 0,
  isExpanded = false, onToggleExpand = null,
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    loadMessages();
  }, [projectId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadMessages = async () => {
    try {
      const res = await chatAPI.getMessages(projectId);
      setMessages(res.data);
    } catch {
      toast.error('Failed to load chat history');
    } finally {
      setFetching(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');

    const tempId = Date.now();
    setMessages((prev) => [...prev, {
      id: tempId, role: 'user', content: text,
      created_at: new Date().toISOString(),
    }]);
    setLoading(true);

    try {
      const res = await chatAPI.sendMessage(projectId, text);
      setMessages((prev) => [...prev, res.data]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send message');
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    } finally {
      setLoading(false);
    }
  };

  const clearChat = async () => {
    if (!window.confirm('Clear all chat history?')) return;
    await chatAPI.clearHistory(projectId);
    setMessages([]);
    toast.success('Chat history cleared');
  };

  if (fetching) return (
    <div className="flex items-center justify-center h-full">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
    </div>
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Toolbar */}
      <div className="flex items-center justify-between pb-3 border-b border-gray-100">
        <div className="flex items-center gap-3">
          <h3 className="font-semibold text-gray-800">AI Chat</h3>
          {totalDocs === 0 ? (
            <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
              No documents uploaded
            </span>
          ) : indexedCount === 0 ? (
            <span className="text-xs text-yellow-700 bg-yellow-50 px-2 py-0.5 rounded-full flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse inline-block" />
              Indexing {totalDocs} document{totalDocs !== 1 ? 's' : ''}…
            </span>
          ) : (
            <span className="text-xs text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
              {indexedCount} document{indexedCount !== 1 ? 's' : ''} ready
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {messages.length > 0 && (
            <button onClick={clearChat} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-gray-50 transition-colors">
              <TrashIcon className="w-3.5 h-3.5" /> Clear
            </button>
          )}
          {onToggleExpand && (
            <button
              onClick={onToggleExpand}
              className="text-xs text-gray-400 hover:text-gray-700 flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
              title={isExpanded ? 'Exit full screen (Esc)' : 'Expand to full screen'}
            >
              {isExpanded
                ? <ArrowsPointingInIcon className="w-4 h-4" />
                : <ArrowsPointingOutIcon className="w-4 h-4" />
              }
            </button>
          )}
        </div>
      </div>

      {/* Messages — flex-1 fills all remaining space; internal scroll only */}
      <div className="flex-1 min-h-0 overflow-y-auto py-6 space-y-8 px-1">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 py-12">
            <p className="text-4xl mb-3">💬</p>
            {totalDocs === 0 ? (
              <>
                <p className="text-sm font-medium text-gray-600">No documents yet</p>
                <p className="text-xs mt-1">Switch to the Documents tab and upload a file to get started</p>
              </>
            ) : indexedCount === 0 ? (
              <>
                <p className="text-sm font-medium text-gray-600">Indexing in progress…</p>
                <p className="text-xs mt-1">Your document{totalDocs !== 1 ? 's are' : ' is'} being processed — this takes up to 30 seconds on first run</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium">Ask anything about your documents</p>
                <p className="text-xs mt-1">{indexedCount} document{indexedCount !== 1 ? 's' : ''} indexed and ready</p>
              </>
            )}
          </div>
        )}
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} projectId={projectId} />
        ))}
        {loading && (
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-sm">🤖</div>
            <div className="bg-gray-100 rounded-2xl rounded-tl-none px-4 py-3">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="border-t border-gray-100 pt-4">
        <div className="flex gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask a question about your documents..."
            rows={3}
            className="flex-1 resize-none rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-transparent"
          />
          <button
            onClick={send}
            disabled={!input.trim() || loading}
            className="px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <PaperAirplaneIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1 ml-1">Shift+Enter for new line · Enter to send</p>
      </div>
    </div>
  );
}

function MessageBubble({ message, projectId }) {
  const isUser = message.role === 'user';
  const [showEmailModal, setShowEmailModal] = useState(false);

  // Assistant screening replies are plain markdown. Detect them and pull out
  // the shortlisted candidate names so we can offer to email interview invites.
  const screening = isUser ? null : parseScreeningMarkdown(message.content);
  const showEmailButton = screening?.isScreening && screening.shortlisted.length > 0;

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${
        isUser ? 'bg-primary-600 text-white' : 'bg-primary-100'
      }`}>
        {isUser ? '👤' : '🤖'}
      </div>
      <div className={`max-w-[80%] ${isUser ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
        <div className={`px-4 py-3 rounded-2xl text-[15px] leading-relaxed ${
          isUser
            ? 'bg-primary-600 text-white rounded-tr-none'
            : 'bg-gray-100 text-gray-800 rounded-tl-none'
        }`}>
          {isUser ? (
            <p>{message.content}</p>
          ) : (
            <div className="prose-chat">
              <ReactMarkdown>{message.content}</ReactMarkdown>
            </div>
          )}
        </div>
        {showEmailButton && (
          <button
            onClick={() => setShowEmailModal(true)}
            className="flex items-center gap-2 self-start px-4 py-2 mt-1 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 transition-colors"
          >
            <EnvelopeIcon className="w-4 h-4" />
            Send Interview Email{screening.shortlisted.length !== 1 ? 's' : ''} ({screening.shortlisted.length})
          </button>
        )}
        {showEmailButton && (
          <SendInterviewEmailModal
            open={showEmailModal}
            onClose={() => setShowEmailModal(false)}
            candidates={screening.shortlisted.map((name) => ({ name, position: screening.position }))}
            projectId={projectId}
            onSent={() => {}}
          />
        )}
        {message.sources?.length > 0 && (
          <div className="flex flex-wrap gap-1 px-1">
            {message.sources.map((s, i) => (
              <span key={i} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded-full">
                📎 {s}
              </span>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400 px-1">
          {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </p>
      </div>
    </div>
  );
}
